import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  CIRCUIT_KINDS,
  CIRCUIT_KIND_LABELS,
  SITE_SURVEY_SECTIONS,
  isAsked,
  isSiteSurveySectionId,
  scopeFor,
  siteFields,
  siteSection,
  siteSectionProgress,
  type Circuit,
  type SiteAnswers,
  type SiteField,
  type SiteSection,
  type SiteSurveySectionId,
} from "@sts/schemas";
import { calculateIndexCircuitSizing, copperTubeInternalDiameterMm, maximumDemand } from "@sts/calc";
import type { PhotoEvidence } from "../api/capture-types.js";
import type { PlanObject, SiteSurveyAnswersBySection, SurveySummary } from "../api/types.js";
import { ApiError } from "../api/client.js";
import { usePortal } from "../app/context.js";
import { useAsync } from "../app/use-async.js";
import { href } from "../app/router.js";
import { Async } from "../components/Async.jsx";
import { PhotoImage } from "../components/PhotoRail.jsx";
import { SupplyBills } from "../components/SupplyBills.jsx";

/**
 * One section of the site survey (migration 0056): what the installer needs
 * on the day — see `@sts/schemas` site-survey.ts for every question.
 *
 * Built to be quicker than a form: most answers are one tap (a choice, a yes
 * or no), photos are picked from the ones the phone already took — the likely
 * ones first — and every answer saves the moment it is given. The panel on the
 * right works things out as the answers go in: the maximum demand and DNO
 * route, whether the unit and the cylinder fit their spaces, the pipe sizes,
 * and what planning asks for.
 */

interface Loaded {
  readonly survey: SurveySummary;
  readonly sections: SiteSurveyAnswersBySection;
  readonly canEdit: boolean;
  readonly photos: readonly PhotoEvidence[];
  readonly objects: readonly PlanObject[];
  readonly heatLossW: number | null;
  /**
   * The design's own flow temperature and flow-return difference, where the
   * designer has set them (`designCheck`'s context). Null falls back to the
   * figures below, and the check says which it used.
   */
  readonly designFlowTempC: number | null;
  readonly systemDeltaTK: number | null;
}

/**
 * What the pipework check assumes when nobody has set a design yet: the
 * flow-return difference a heat pump primary is commonly designed to, and the
 * mean water temperature of a 45/40 circuit. Both are the platform's, not a
 * standard's, and the check prints "assumed" beside a figure it had to guess.
 */
const ASSUMED_DELTA_T_K = 5;
const ASSUMED_MEAN_WATER_C = 42.5;

/**
 * The water temperatures the pipework check sizes against: the design's own
 * where a designer has set them, the assumptions above where they have not.
 *
 * Its own function because it is the part with a decision in it. A pipe that
 * passes at 5 K can fail at 7 K — the same kilowatts through less water — so
 * a check run against an assumed difference is a check of a system nobody is
 * building, and `assumed` is what makes the screen say so.
 *
 * The mean water temperature follows from the pair: a 50 °C flow at 7 K
 * returns at 43 °C and averages 46.5 °C. It only moves the viscosity a little,
 * but it is free to get right once the flow temperature is known.
 */
export function pipeSizingBasis(design: {
  readonly designFlowTempC: number | null;
  readonly systemDeltaTK: number | null;
}): { readonly deltaTK: number; readonly meanWaterC: number; readonly assumed: boolean } {
  const deltaTK = design.systemDeltaTK ?? ASSUMED_DELTA_T_K;
  return {
    deltaTK,
    meanWaterC: design.designFlowTempC !== null ? design.designFlowTempC - deltaTK / 2 : ASSUMED_MEAN_WATER_C,
    assumed: design.systemDeltaTK === null || design.designFlowTempC === null,
  };
}

export function SiteSurvey({ surveyId, section }: { readonly surveyId: string; readonly section: string }): ReactNode {
  const { client } = usePortal();
  const { state, reload } = useAsync<Loaded>(async () => {
    const [survey, sections, me] = await Promise.all([client.getSurvey(surveyId), client.siteSurvey(surveyId), client.me()]);
    const [photos, objects, heatLoss, designCheck] = await Promise.allSettled([
      client.jobPhotos(survey.jobId),
      client.planObjects(surveyId),
      client.heatLoss(surveyId),
      client.designCheck(surveyId),
    ]);
    const design = designCheck.status === "fulfilled" ? designCheck.value.context : null;
    return {
      survey,
      sections,
      canEdit: scopeFor(me.role, "survey", "update") !== "none" && survey.status !== "superseded",
      photos: photos.status === "fulfilled" ? photos.value : [],
      objects: objects.status === "fulfilled" ? objects.value : [],
      heatLossW: heatLoss.status === "fulfilled" ? heatLoss.value.heatLoss.totalHeatLossW : null,
      designFlowTempC: design?.designFlowTempC ?? null,
      systemDeltaTK: design?.systemDeltaTK ?? null,
    };
  }, [client, surveyId]);

  if (!isSiteSurveySectionId(section)) {
    return (
      <div className="page-header">
        <h1>Site survey</h1>
        <p className="lede">There is no section called “{section}”.</p>
        <a href={href.survey(surveyId)}>Back to the survey</a>
      </div>
    );
  }
  return (
    <Async state={state} what="the site survey" reload={reload}>
      {(data) => <Workspace surveyId={surveyId} sectionId={section} data={data} />}
    </Async>
  );
}

function Workspace({
  surveyId,
  sectionId,
  data,
}: {
  readonly surveyId: string;
  readonly sectionId: SiteSurveySectionId;
  readonly data: Loaded;
}): ReactNode {
  const { client } = usePortal();
  const [answers, setAnswers] = useState<Record<string, SiteAnswers>>(() =>
    Object.fromEntries(SITE_SURVEY_SECTIONS.map((s) => [s.id, (data.sections[s.id]?.answers ?? {}) as SiteAnswers])),
  );
  const [saving, setSaving] = useState(0);
  const [error, setError] = useState<string | null>(null);
  // Taps save one after another without waiting. Only the answer to the
  // LATEST save is laid over the screen: an earlier one, arriving late, would
  // take back a tap made after it.
  const latest = useRef(0);
  const section = siteSection(sectionId);
  const mine = answers[sectionId] ?? {};
  const progress = siteSectionProgress(section, mine);

  const save = (patch: Readonly<Record<string, unknown>>): void => {
    if (!data.canEdit) return;
    const before = answers[sectionId] ?? {};
    const next: Record<string, unknown> = { ...before };
    for (const [k, v] of Object.entries(patch)) {
      if (v === null) delete next[k];
      else next[k] = v;
    }
    setAnswers((all) => ({ ...all, [sectionId]: next as SiteAnswers }));
    setError(null);
    setSaving((n) => n + 1);
    const mine = ++latest.current;
    client
      .patchSiteSurvey(surveyId, sectionId, patch)
      .then((saved) => {
        if (mine === latest.current) setAnswers((all) => ({ ...all, [sectionId]: saved.answers as SiteAnswers }));
      })
      .catch((e: unknown) => {
        setError(e instanceof ApiError ? e.message : "The answer could not be saved. Check the connection and try again.");
        // What the server holds is the truth: read the section back rather
        // than guess which of several taps the refusal undid.
        client
          .siteSurvey(surveyId)
          .then((fresh) => setAnswers((all) => ({ ...all, [sectionId]: (fresh[sectionId]?.answers ?? before) as SiteAnswers })))
          .catch(() => setAnswers((all) => ({ ...all, [sectionId]: before })));
      })
      .finally(() => setSaving((n) => n - 1));
  };

  const order = SITE_SURVEY_SECTIONS.map((s) => s.id);
  const nextId = order[order.indexOf(sectionId) + 1];
  const firstMissing = progress.missing[0];
  const missingLabel = firstMissing ? siteFields(section).find((f) => f.id === firstMissing)?.label : undefined;

  return (
    <div className="site-survey" data-testid="site-survey">
      <div className="page-header">
        <p className="small">
          <a href={href.survey(surveyId)}>Back to the survey</a>
        </p>
        <div className="site-survey__title">
          <h1>{section.title}</h1>
          <span className="muted small" role="status" data-testid="site-survey-saved">
            {!data.canEdit ? "Read only" : saving > 0 ? "Saving…" : "Saved"}
          </span>
        </div>
        <p className="lede">{section.blurb}</p>
      </div>

      <div className="site-survey__layout">
        <nav className="site-survey__nav" aria-label="Site survey sections">
          {SITE_SURVEY_SECTIONS.map((s) => {
            const p = siteSectionProgress(s, answers[s.id] ?? {});
            const touched = Object.keys(answers[s.id] ?? {}).length > 0;
            const tone = p.required === 0 ? (touched ? "done" : "optional") : p.complete ? "done" : touched ? "part" : "todo";
            return (
              <a
                key={s.id}
                href={href.surveySite(surveyId, s.id)}
                className={`site-survey__nav-item${s.id === sectionId ? " is-current" : ""}`}
                aria-current={s.id === sectionId ? "page" : undefined}
              >
                <span className="site-survey__nav-title">{s.title}</span>
                <span className={`site-survey__dot site-survey__dot--${tone}`} aria-label={statusWords(p, touched)} />
              </a>
            );
          })}
        </nav>

        <div className="site-survey__form">
          {error ? (
            <p className="notice error" role="alert" data-testid="site-survey-error">
              {error}
            </p>
          ) : null}
          {section.groups.map((group) => {
            const asked = group.fields.filter((f) => isAsked(f, mine));
            if (asked.length === 0) return null;
            return (
              <section key={group.title} className="site-survey__group">
                <h2>{group.title}</h2>
                {asked.map((field) => (
                  <Question
                    key={field.id}
                    field={field}
                    value={mine[field.id]}
                    disabled={!data.canEdit}
                    photos={data.photos}
                    rooms={data.survey.rooms.map((r) => r.name)}
                    onAnswer={(value) => save({ [field.id]: value })}
                  />
                ))}
              </section>
            );
          })}
          {sectionId === "energy" ? <SupplyBills surveyId={surveyId} /> : null}
        </div>

        <aside className="site-survey__side" aria-label="Checks">
          <div className="site-survey__card">
            <h2>Progress</h2>
            {progress.required === 0 ? (
              <p className="muted small">Nothing here is required — answer what you know.</p>
            ) : (
              <>
                <div className="site-survey__bar" aria-hidden="true">
                  <span style={{ width: `${Math.round((progress.answered / progress.required) * 100)}%` }} />
                </div>
                <p className="small" data-testid="site-survey-progress">
                  {progress.answered} of {progress.required} required answered
                </p>
                {firstMissing ? (
                  <a className="site-survey__jump" href={`#q-${firstMissing}`} onClick={(e) => jumpTo(e, firstMissing)}>
                    Next: {missingLabel}
                  </a>
                ) : (
                  <p className="small site-survey__ok">All the required answers are in.</p>
                )}
              </>
            )}
          </div>
          <LiveChecks sectionId={sectionId} answers={mine} all={answers} data={data} />
          {nextId ? (
            <a className="cta site-survey__next" href={href.surveySite(surveyId, nextId)}>
              Next: {siteSection(nextId).title}
            </a>
          ) : (
            <a className="cta site-survey__next" href={href.survey(surveyId)}>
              Back to the survey
            </a>
          )}
        </aside>
      </div>
    </div>
  );
}

function statusWords(p: { answered: number; required: number; complete: boolean }, touched: boolean): string {
  if (p.required === 0) return touched ? "Answered" : "Optional";
  if (p.complete) return "Done";
  return touched ? `${p.answered} of ${p.required} answered` : "Not started";
}

/** Scrolls the question into view and puts focus on its first control. */
function jumpTo(event: { preventDefault: () => void }, id: string): void {
  event.preventDefault();
  const el = document.getElementById(`q-${id}`);
  el?.scrollIntoView?.({ behavior: "smooth", block: "center" });
  el?.querySelector<HTMLElement>("button, input, textarea, select")?.focus();
}

// ------------------------------------------------------------ questions ----

function Question({
  field,
  value,
  disabled,
  photos,
  rooms,
  onAnswer,
}: {
  readonly field: SiteField;
  readonly value: unknown;
  readonly disabled: boolean;
  readonly photos: readonly PhotoEvidence[];
  readonly rooms: readonly string[];
  readonly onAnswer: (value: unknown) => void;
}): ReactNode {
  const labelId = `q-${field.id}-label`;
  return (
    <div className="site-survey__q" id={`q-${field.id}`} data-testid={`q-${field.id}`}>
      <div className="site-survey__q-label" id={labelId}>
        {field.label}
        {field.unit ? <span className="muted"> ({field.unit})</span> : null}
        {field.required && (value === undefined || value === "" || (Array.isArray(value) && value.length === 0)) ? (
          <span className="site-survey__needed">Needed</span>
        ) : null}
      </div>
      <Control field={field} value={value} disabled={disabled} photos={photos} rooms={rooms} onAnswer={onAnswer} labelId={labelId} />
      {field.help ? <div className="site-survey__help">{field.help}</div> : null}
    </div>
  );
}

function Control({
  field,
  value,
  disabled,
  photos,
  rooms,
  onAnswer,
  labelId,
}: {
  readonly field: SiteField;
  readonly value: unknown;
  readonly disabled: boolean;
  readonly photos: readonly PhotoEvidence[];
  readonly rooms: readonly string[];
  readonly onAnswer: (value: unknown) => void;
  readonly labelId: string;
}): ReactNode {
  switch (field.kind) {
    case "choice":
      return (
        <div className="site-survey__chips" role="group" aria-labelledby={labelId}>
          {(field.options ?? []).map((o) => (
            <button
              key={o.value}
              type="button"
              className="site-survey__chip"
              aria-pressed={value === o.value}
              disabled={disabled}
              title={o.help}
              onClick={() => onAnswer(value === o.value ? null : o.value)}
            >
              {o.label}
              {o.help && value === o.value ? <span className="site-survey__chip-help">{o.help}</span> : null}
            </button>
          ))}
        </div>
      );
    case "multi": {
      const picked = Array.isArray(value) ? (value as string[]) : [];
      return (
        <div className="site-survey__chips" role="group" aria-labelledby={labelId}>
          {(field.options ?? []).map((o) => {
            const on = picked.includes(o.value);
            return (
              <button
                key={o.value}
                type="button"
                className="site-survey__chip"
                aria-pressed={on}
                disabled={disabled}
                onClick={() => {
                  const next = on ? picked.filter((v) => v !== o.value) : [...picked, o.value];
                  onAnswer(next.length === 0 ? null : next);
                }}
              >
                {o.label}
              </button>
            );
          })}
        </div>
      );
    }
    case "yes-no":
      return (
        <div className="site-survey__chips" role="group" aria-labelledby={labelId}>
          {[true, false].map((v) => (
            <button
              key={String(v)}
              type="button"
              className="site-survey__chip"
              aria-pressed={value === v}
              disabled={disabled}
              onClick={() => onAnswer(value === v ? null : v)}
            >
              {v ? "Yes" : "No"}
            </button>
          ))}
        </div>
      );
    case "number":
      return <NumberInput field={field} value={value} disabled={disabled} onAnswer={onAnswer} labelId={labelId} />;
    case "text":
    case "long-text":
      return (
        <TextInput
          field={field}
          value={value}
          disabled={disabled}
          onAnswer={onAnswer}
          labelId={labelId}
          suggestions={field.id === "index_room" ? rooms : []}
        />
      );
    case "photos":
      return <PhotoPicker field={field} value={value} disabled={disabled} photos={photos} onAnswer={onAnswer} />;
    case "circuits":
      return <Circuits value={value} disabled={disabled} onAnswer={onAnswer} />;
  }
}

function NumberInput({
  field,
  value,
  disabled,
  onAnswer,
  labelId,
}: {
  readonly field: SiteField;
  readonly value: unknown;
  readonly disabled: boolean;
  readonly onAnswer: (value: unknown) => void;
  readonly labelId: string;
}): ReactNode {
  const saved = typeof value === "number" ? String(value) : "";
  const [draft, setDraft] = useState(saved);
  const [problem, setProblem] = useState<string | null>(null);
  useEffect(() => setDraft(saved), [saved]);
  const commit = (): void => {
    const text = draft.trim();
    if (text === saved) return;
    if (text === "") {
      setProblem(null);
      onAnswer(null);
      return;
    }
    const n = Number(text.replace(",", "."));
    if (!Number.isFinite(n)) return setProblem("Type a number.");
    if (field.min !== undefined && n < field.min) return setProblem(`At least ${field.min}${field.unit ? ` ${field.unit}` : ""}.`);
    if (field.max !== undefined && n > field.max) return setProblem(`At most ${field.max}${field.unit ? ` ${field.unit}` : ""}.`);
    setProblem(null);
    onAnswer(n);
  };
  return (
    <div className="site-survey__number">
      <input
        type="text"
        inputMode="decimal"
        aria-labelledby={labelId}
        value={draft}
        disabled={disabled}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
        }}
      />
      {field.unit ? <span className="muted">{field.unit}</span> : null}
      {problem ? <span className="site-survey__problem" role="alert">{problem}</span> : null}
    </div>
  );
}

function TextInput({
  field,
  value,
  disabled,
  onAnswer,
  labelId,
  suggestions,
}: {
  readonly field: SiteField;
  readonly value: unknown;
  readonly disabled: boolean;
  readonly onAnswer: (value: unknown) => void;
  readonly labelId: string;
  readonly suggestions: readonly string[];
}): ReactNode {
  const saved = typeof value === "string" ? value : "";
  const [draft, setDraft] = useState(saved);
  useEffect(() => setDraft(saved), [saved]);
  const commit = (text = draft): void => {
    if (text.trim() === saved.trim()) return;
    onAnswer(text.trim() === "" ? null : text.trim());
  };
  const listId = suggestions.length > 0 ? `${labelId}-list` : undefined;
  return field.kind === "long-text" ? (
    <textarea
      className="site-survey__text"
      aria-labelledby={labelId}
      rows={3}
      value={draft}
      disabled={disabled}
      maxLength={field.maxLength ?? 4000}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => commit()}
    />
  ) : (
    <>
      <input
        className="site-survey__text"
        type="text"
        aria-labelledby={labelId}
        value={draft}
        disabled={disabled}
        maxLength={field.maxLength ?? 200}
        list={listId}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => commit()}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
        }}
      />
      {listId ? (
        <datalist id={listId}>
          {suggestions.map((s) => (
            <option key={s} value={s} />
          ))}
        </datalist>
      ) : null}
    </>
  );
}

/** A photo whose note mentions one of the question's words — the likely ones for it. */
export function suggestedPhotos(field: SiteField, photos: readonly PhotoEvidence[]): readonly PhotoEvidence[] {
  const hints = (field.photoHints ?? []).map((h) => h.toLowerCase());
  if (hints.length === 0) return [];
  return photos.filter((p) => {
    const words = `${p.subject} ${p.description ?? ""} ${p.capturedDescription ?? ""}`.toLowerCase();
    return hints.some((h) => words.includes(h));
  });
}

export function PhotoPicker({
  field,
  value,
  disabled,
  photos,
  onAnswer,
}: {
  readonly field: SiteField;
  readonly value: unknown;
  readonly disabled: boolean;
  readonly photos: readonly PhotoEvidence[];
  readonly onAnswer: (value: unknown) => void;
}): ReactNode {
  const picked = Array.isArray(value) ? (value as string[]) : [];
  const [open, setOpen] = useState(false);
  const suggested = useMemo(() => new Set(suggestedPhotos(field, photos).map((p) => p.id)), [field, photos]);
  const ordered = useMemo(
    () => [...photos].sort((a, b) => Number(suggested.has(b.id)) - Number(suggested.has(a.id))),
    [photos, suggested],
  );
  const toggle = (id: string): void => {
    const next = picked.includes(id) ? picked.filter((p) => p !== id) : [...picked, id];
    onAnswer(next.length === 0 ? null : next);
  };
  const byId = new Map(photos.map((p) => [p.id, p]));
  return (
    <div className="site-survey__photos">
      <div className="site-survey__photo-row">
        {picked.map((id) => {
          const photo = byId.get(id);
          return (
            <figure key={id} className="site-survey__photo">
              {photo ? <PhotoImage photo={photo} /> : <span className="photo-rail__thumb photo-rail__thumb--failed">Gone</span>}
              {disabled ? null : (
                <button type="button" className="site-survey__photo-remove" aria-label="Remove this photo" onClick={() => toggle(id)}>
                  ×
                </button>
              )}
            </figure>
          );
        })}
        {disabled ? null : (
          <button type="button" className="site-survey__photo-add" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
            {open ? "Done" : picked.length === 0 ? "Pick from the phone's photos" : "Add or remove"}
          </button>
        )}
      </div>
      {open ? (
        photos.length === 0 ? (
          <p className="muted small">No photos have been synced from the phone for this job yet.</p>
        ) : (
          <div className="site-survey__photo-grid" role="group" aria-label={`Photos for ${field.label}`}>
            {ordered.map((p) => (
              <button
                key={p.id}
                type="button"
                className="site-survey__photo-choice"
                aria-pressed={picked.includes(p.id)}
                title={p.subject}
                onClick={() => toggle(p.id)}
              >
                <PhotoImage photo={p} />
                {suggested.has(p.id) ? <span className="site-survey__suggested">Likely</span> : null}
                <span className="site-survey__photo-caption">{p.subject || "Photo"}</span>
              </button>
            ))}
          </div>
        )
      ) : null}
    </div>
  );
}

function Circuits({
  value,
  disabled,
  onAnswer,
}: {
  readonly value: unknown;
  readonly disabled: boolean;
  readonly onAnswer: (value: unknown) => void;
}): ReactNode {
  const saved = Array.isArray(value) ? (value as Circuit[]) : [];
  const [rows, setRows] = useState<Circuit[]>(saved);
  useEffect(() => setRows(saved), [JSON.stringify(saved)]); // eslint-disable-line react-hooks/exhaustive-deps
  const commit = (next: Circuit[]): void => {
    const clean = next.filter((c) => c.ratingA > 0);
    onAnswer(clean.length === 0 ? null : clean);
  };
  const edit = (i: number, patch: Partial<Circuit>, save = false): void => {
    const next = rows.map((r, j) => {
      if (j !== i) return r;
      const merged = { ...r, ...patch };
      if (patch.loadA === undefined && "loadA" in patch) {
        const { loadA: _drop, ...rest } = merged;
        return rest as Circuit;
      }
      return merged;
    });
    setRows(next);
    if (save) commit(next);
  };
  const hasHeatPump = rows.some((r) => r.kind === "heat-pump");
  return (
    <div className="site-survey__circuits">
      <table>
        <thead>
          <tr>
            <th scope="col">Circuit</th>
            <th scope="col">Kind</th>
            <th scope="col">Breaker, A</th>
            <th scope="col">Draws, A (if known)</th>
            <th scope="col">
              <span className="visually-hidden">Remove</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td>
                <input
                  aria-label={`Circuit ${i + 1} name`}
                  value={r.name}
                  disabled={disabled}
                  maxLength={60}
                  onChange={(e) => edit(i, { name: e.target.value })}
                  onBlur={() => commit(rows)}
                />
              </td>
              <td>
                <select
                  aria-label={`Circuit ${i + 1} kind`}
                  value={r.kind}
                  disabled={disabled}
                  onChange={(e) => edit(i, { kind: e.target.value as Circuit["kind"] }, true)}
                >
                  {CIRCUIT_KINDS.map((k) => (
                    <option key={k} value={k}>
                      {CIRCUIT_KIND_LABELS[k]}
                    </option>
                  ))}
                </select>
              </td>
              <td>
                <input
                  aria-label={`Circuit ${i + 1} breaker rating`}
                  inputMode="decimal"
                  value={r.ratingA ? String(r.ratingA) : ""}
                  disabled={disabled}
                  onChange={(e) => edit(i, { ratingA: Number(e.target.value) || 0 })}
                  onBlur={() => commit(rows)}
                />
              </td>
              <td>
                <input
                  aria-label={`Circuit ${i + 1} current drawn`}
                  inputMode="decimal"
                  value={r.loadA === undefined ? "" : String(r.loadA)}
                  disabled={disabled}
                  onChange={(e) => edit(i, e.target.value.trim() === "" ? { loadA: undefined } : { loadA: Number(e.target.value) || 0 })}
                  onBlur={() => commit(rows)}
                />
              </td>
              <td>
                {disabled ? null : (
                  <button type="button" className="floor-plan__link" onClick={() => {
                    const next = rows.filter((_, j) => j !== i);
                    setRows(next);
                    commit(next);
                  }}>
                    Remove
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {disabled ? null : (
        <div className="site-survey__chips">
          <button type="button" className="site-survey__chip" onClick={() => setRows([...rows, { name: "", kind: "sockets", ratingA: 32 }])}>
            + Add a circuit
          </button>
          {hasHeatPump ? null : (
            <button
              type="button"
              className="site-survey__chip"
              onClick={() => {
                const next = [...rows, { name: "Heat pump (new)", kind: "heat-pump" as const, ratingA: 20 }];
                setRows(next);
                commit(next);
              }}
            >
              + Add the new heat pump
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------- live checks ----

type Tone = "ok" | "warn" | "bad" | "info";

function Check({ tone, title, children }: { readonly tone: Tone; readonly title: string; readonly children?: ReactNode }): ReactNode {
  return (
    <div className={`site-survey__check site-survey__check--${tone}`}>
      <span className="site-survey__check-dot" aria-hidden="true" />
      <div>
        <div className="site-survey__check-title">{title}</div>
        {children ? <div className="site-survey__check-body">{children}</div> : null}
      </div>
    </div>
  );
}

const num = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? v : null);

function LiveChecks({
  sectionId,
  answers,
  all,
  data,
}: {
  readonly sectionId: SiteSurveySectionId;
  readonly answers: SiteAnswers;
  readonly all: Record<string, SiteAnswers>;
  readonly data: Loaded;
}): ReactNode {
  const checks = liveChecks(sectionId, answers, all, data);
  if (checks.length === 0) return null;
  return (
    <div className="site-survey__card" data-testid="site-survey-checks">
      <h2>Live checks</h2>
      {checks}
    </div>
  );
}

function liveChecks(
  sectionId: SiteSurveySectionId,
  a: SiteAnswers,
  all: Record<string, SiteAnswers>,
  data: Loaded,
): ReactNode[] {
  const out: ReactNode[] = [];
  const heatPump = data.objects.find((o) => o.kind === "heat-pump");
  const cylinder = data.objects.find((o) => o.kind === "cylinder");
  switch (sectionId) {
    case "heat-pump": {
      if (heatPump && heatPump.kind === "heat-pump") {
        const s = heatPump.spec;
        out.push(
          <Check key="unit" tone="info" title={s.name}>
            {s.widthMm} × {s.heightMm} × {s.depthMm} mm, on the plan
            {s.design?.soundPowerDbA ? ` · sound power ${s.design.soundPowerDbA} dB(A)` : ""}
          </Check>,
        );
        const w = num(a["space_width_mm"]);
        const h = num(a["space_height_mm"]);
        const d = num(a["space_depth_mm"]);
        if (w !== null && h !== null && d !== null) {
          const short = [
            w < s.widthMm ? `width ${w} < ${s.widthMm} mm` : null,
            h < s.heightMm ? `height ${h} < ${s.heightMm} mm` : null,
            d < s.depthMm ? `depth ${d} < ${s.depthMm} mm` : null,
          ].filter(Boolean);
          out.push(
            short.length === 0 ? (
              <Check key="space" tone="ok" title="The unit fits the space">
                Leave the manufacturer's clearances round it too.
              </Check>
            ) : (
              <Check key="space" tone="bad" title="Too small for the unit">
                {short.join(", ")}.
              </Check>
            ),
          );
        }
        const capacity = s.design?.capacityAtDesignW;
        if (capacity && data.heatLossW) {
          out.push(
            <Check
              key="output"
              tone={capacity >= data.heatLossW ? "ok" : "bad"}
              title={capacity >= data.heatLossW ? "Output covers the heat loss" : "Output is short of the heat loss"}
            >
              {(capacity / 1000).toFixed(2)} kW at design against {(data.heatLossW / 1000).toFixed(2)} kW.
            </Check>,
          );
        }
        const positions = s.design?.noisePositions?.length ?? 0;
        out.push(
          <Check key="noise" tone={positions > 0 ? "ok" : "warn"} title={positions > 0 ? `${positions} noise position${positions === 1 ? "" : "s"} on the plan` : "No noise positions yet"}>
            The MCS 020(a) result is worked out on the plan's design check.
          </Check>,
        );
      } else {
        out.push(
          <Check key="unit" tone="warn" title="No heat pump on the plan yet">
            Put one on the floor plan and its size, noise and output are checked here.
          </Check>,
        );
      }
      const flags = [
        a["listed"] === true ? "a listed building" : null,
        a["conservation"] === true ? "a conservation area" : null,
        a["fronts_highway"] === true ? "facing a road" : null,
        a["above_ground"] === true ? "above ground floor" : null,
      ].filter(Boolean);
      const planningAnswered = ["listed", "conservation", "fronts_highway", "above_ground"].every((k) => typeof a[k] === "boolean");
      if (flags.length > 0) {
        out.push(
          <Check key="planning" tone="warn" title="Planning permission may be needed">
            Permitted development does not cover {flags.join(", ")}: check with the council.
          </Check>,
        );
      } else if (planningAnswered) {
        out.push(
          <Check key="planning" tone="ok" title="Permitted development looks likely">
            Subject to the noise assessment passing.
          </Check>,
        );
      }
      break;
    }
    case "electrics": {
      const circuits = Array.isArray(a["circuits"]) ? (a["circuits"] as Circuit[]) : [];
      const fuse = typeof a["fuse_a"] === "string" && a["fuse_a"] !== "unknown" ? Number(a["fuse_a"]) : null;
      const d = maximumDemand({
        circuits,
        fuseA: fuse,
        looped: a["looped"] === true,
        safetyConcern: a["safety_concern"] === true,
      });
      out.push(
        <Check key="demand" tone="info" title={`Maximum demand ${d.totalA} A`}>
          Existing {d.existingA} A after diversity, heat pump {d.heatPumpA} A
          {fuse ? `, of the ${fuse} A fuse (${Math.round((d.fuseShare ?? 0) * 100)}%)` : ""}.
          {num(a["measured_demand_a"]) !== null ? ` Measured peak ${num(a["measured_demand_a"])} A.` : ""}
        </Check>,
      );
      out.push(
        <Check
          key="route"
          tone={d.route === "connect-and-notify" ? (d.tight ? "warn" : "ok") : d.route === "apply-first" ? "bad" : "warn"}
          title={d.route === "connect-and-notify" ? "DNO: connect and notify" : d.route === "apply-first" ? "DNO: apply before the work" : "DNO route not known yet"}
        >
          {d.reasons.join(" ")}
        </Check>,
      );
      if (a["spare_way"] === false) {
        out.push(<Check key="way" tone="warn" title="No spare way">The consumer unit needs changing or an extra enclosure.</Check>);
      }
      break;
    }
    case "cylinder": {
      if (cylinder && cylinder.kind === "cylinder") {
        const s = cylinder.spec;
        out.push(<Check key="cyl" tone="info" title={s.name}>{s.volumeLitres} L · {s.diameterMm} mm across, {s.heightMm} mm tall, on the plan</Check>);
        const w = num(a["cyl_space_width_mm"]);
        const dd = num(a["cyl_space_depth_mm"]);
        const h = num(a["cyl_space_height_mm"]);
        if (w !== null && dd !== null && h !== null) {
          const fits = w >= s.diameterMm && dd >= s.diameterMm && h >= s.heightMm;
          out.push(
            <Check key="fit" tone={fits ? "ok" : "bad"} title={fits ? "The cylinder fits the space" : "Too small for the cylinder"}>
              {fits ? "Allow room for its pipework and tappings." : `Needs ${s.diameterMm} × ${s.diameterMm} × ${s.heightMm} mm.`}
            </Check>,
          );
        }
      } else {
        out.push(<Check key="cyl" tone="warn" title="No cylinder on the plan yet">Put one on the floor plan and its fit is checked here.</Check>);
      }
      break;
    }
    case "pipework": {
      const size = (key: string): number | null => {
        const v = a[key];
        return typeof v === "string" && v.startsWith("cu-") ? Number(v.slice(3)) : null;
      };
      const seg = (pipe: string, length: string, id: string) => {
        const od = size(pipe);
        const l = num(a[length]);
        if (od === null || l === null) return null;
        try {
          return { id, internalDiameterMm: copperTubeInternalDiameterMm(od, "X"), oneWayLengthM: l, material: "copper" as const };
        } catch {
          return null;
        }
      };
      const primary = seg("primary_pipe", "primary_length_m", "Primary");
      if (primary && data.heatLossW) {
        const segments = [primary, seg("branch_pipe", "branch_length_m", "Branch"), seg("tail_pipe", "tail_length_m", "Radiator tail")].filter(
          (s): s is NonNullable<typeof s> => s !== null,
        );
        try {
          // The design's own figures where it has them (`pipeSizingBasis`).
          const { deltaTK, meanWaterC, assumed } = pipeSizingBasis(data);
          const r = calculateIndexCircuitSizing({
            designHeatLoadW: data.heatLossW,
            designFlowReturnDeltaTK: deltaTK,
            segments: [primary],
            meanFluidTempC: meanWaterC,
            complianceMode: false,
            calculationDate: new Date().toISOString().slice(0, 10),
          });
          out.push(
            <Check key="primary" tone={r.velocityWithinLimit && r.pressureGradientWithinLimit ? "ok" : "bad"} title={`Primary: ${r.peakVelocityMPerS.toFixed(2)} m/s`}>
              {r.designFlowRateLitresPerMin.toFixed(1)} L/min for {(data.heatLossW / 1000).toFixed(2)} kW at a {deltaTK} K difference
              {assumed ? " (assumed \u{2014} no design flow temperature set yet)" : ""},{" "}
              {Math.round(r.peakPressureGradientPaPerM)} Pa/m.{" "}
              {r.velocityWithinLimit && r.pressureGradientWithinLimit ? "Within the usual limits." : "Faster than the usual limits: go up a size."}
            </Check>,
          );
          if (segments.length > 1) {
            const whole = calculateIndexCircuitSizing({
              designHeatLoadW: data.heatLossW,
              designFlowReturnDeltaTK: deltaTK,
              segments,
              meanFluidTempC: meanWaterC,
              complianceMode: false,
              calculationDate: new Date().toISOString().slice(0, 10),
            });
            out.push(
              <Check key="index" tone="info" title={`Index circuit: ${whole.circulatorDutyPressureDropKPa.toFixed(1)} kPa`}>
                The pump's duty point, taking the whole house's flow through every length — on the safe side.
              </Check>,
            );
          }
        } catch {
          // A figure the calculation refuses says nothing useful here.
        }
      } else {
        out.push(<Check key="need" tone="info" title="Pick the primary pipe and its length">The flow, speed and pressure drop are worked out here.</Check>);
      }
      break;
    }
    case "existing-heating": {
      const hpAnswers = all["heat-pump"] ?? {};
      if (a["removal"] === "leave") {
        out.push(<Check key="backup" tone="info" title="Kept as a back-up">Say so on the handover: a heat pump must be the main heating for the BUS grant.</Check>);
      }
      if (a["fuel"] === "none" && Object.keys(hpAnswers).length > 0) {
        out.push(<Check key="none" tone="info" title="No heating now">Nothing to take out.</Check>);
      }
      break;
    }
    case "energy": {
      const kwh = num(a["fuel_kwh"]);
      const price = num(a["fuel_price_p"]);
      if (kwh !== null && price !== null) {
        out.push(
          <Check key="cost" tone="info" title={`About £${Math.round((kwh * price) / 100).toLocaleString("en-GB")} a year on heating fuel now`}>
            {kwh.toLocaleString("en-GB")} kWh at {price} p/kWh.
          </Check>,
        );
      }
      break;
    }
    default:
      break;
  }
  return out;
}

/** Every section with its progress — for the Survey page's card. */
export function siteSurveyOverview(
  sections: SiteSurveyAnswersBySection,
): readonly { readonly section: SiteSection; readonly answered: number; readonly required: number; readonly complete: boolean; readonly touched: boolean }[] {
  return SITE_SURVEY_SECTIONS.map((section) => {
    const answers = (sections[section.id]?.answers ?? {}) as SiteAnswers;
    const p = siteSectionProgress(section, answers);
    return { section, answered: p.answered, required: p.required, complete: p.complete, touched: Object.keys(answers).length > 0 };
  });
}
